<script lang="ts" setup>
import './scss/Home.scss';
import './scss/ContratacionPaqueteria.scss';
import { ref, onMounted, ComponentPublicInstance } from 'vue';
import Footer from './components/Footer.vue';
import {
  findCompaniesGPS,
  createOrUpdateClient,
  getPDFCoverage,
  createPolicyPackage,
} from '../../../repositories/landing.repository';
import { evaluateNames, formatterRUT, validationCUIT } from '../../../helpers';
import storePolicy from '@store/policy';
import { useRoute, useRouter } from 'vue-router';
import { BModal } from 'bootstrap-vue-next';
import WhatsappButton from './components/WhatsappButton.vue';
import FormRFCMX from './components/forms/FormRFCMX.vue';
import FormClientMX from './components/forms/FormClientMX.vue';
import FormRUTCL from './components/forms/FormRUTCL.vue';
import FormClientCL from './components/forms/FormClientCL.vue';
import FormCUITAR from './components/forms/FormCUITAR.vue';
import FormClientAR from './components/forms/FormClientAR.vue';
import { useI18n } from 'vue-i18n'
import CaptchaCloudflare from './components/CaptchaCloudflare.vue';

type CaptchaExpose = {
  reset: () => void
}

const { t } = useI18n();
const store = storePolicy();
const router = useRouter();
const route = useRoute();
const refForm3 = ref<any>(null);
const inputsHidden = ref<boolean>(true);
const isOpenModal = ref<boolean>(false);
const loading = ref<boolean>(false);
const policyId = ref<number | null>(1);
const error = ref<string>('');
const companySelectedByParams = ref<{ nombre?: string; id: number } | null>(
  null,
);
const currentDomainCountry = ref<string | null>(null);
const formClientMXref = ref<InstanceType<typeof FormClientMX> | null>(null);
const formClientCLref = ref<InstanceType<typeof FormClientCL> | null>(null);
const formClientARref = ref<InstanceType<typeof FormClientAR> | null>(null);
const tokenCaptcha = ref<string | null>(null);
const captchaRef = ref<ComponentPublicInstance<CaptchaExpose> | null>(null)

onMounted(() => {
  if (!store.data.indemnificationLimit) {
    return router.push('/');
  }
  currentDomainCountry.value = route.meta.subDomainInitial as string;
});

onMounted(async () => {
  const comapaniesGPS = await findCompaniesGPS();
  let formRef;

  if (currentDomainCountry.value === 'MEXICO') {
    formRef = formClientMXref;
  } else if (currentDomainCountry.value === 'ARGENTINA') {
    formRef = formClientARref;
  } else {
    formRef = formClientCLref;
  }

  if (!formRef.value) return;

  formRef.value.fieldsForm2 = formRef.value.fieldsForm2.map((field: any) => {
    if (field.key === 'companyGPSId') {
      field.options = comapaniesGPS;
      const { gpsCompany } = store.data.queryParams;
      const company = comapaniesGPS.find((company) => {
        return company.slug === gpsCompany;
      });
      if (company && gpsCompany) {
        companySelectedByParams.value = company;
        field.value = company;
        field.disabled = true;
        field.customClass =
          'multiselect--disabled .multiselect__select inputLanding';
      }
    }
    return field;
  });
});

const fieldsForm3: any = ref([
  {
    key: 'indemnificationLimit',
    title: t('landing.paqueteria.contratacion_paqueteria.limite_de_indemnizacion'),
    type: 'number',
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    placeholder: t('landing.paqueteria.contratacion_paqueteria.limite_de_indemnizacion'),
    value: store.data.indemnificationLimit,
    cols: 6,
    customClass: 'inputLanding',
    disabled: true,
  },
  {
    key: 'indemnificationLimit-calc',
    title: t('landing.paqueteria.contratacion_paqueteria.precio'),
    type: 'text',
    value: `$${store.data.endPrice} + IVA`,
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    placeholder: t('landing.paqueteria.contratacion_paqueteria.precio_mas_iva'),
    disabled: true,
    cols: 6,
    customClass: 'inputLanding',
  },
  {
    key: 'packageNumbers',
    title: t('landing.paqueteria.contratacion_paqueteria.numero_de_guias_separados_por_comas'),
    type: 'textarea',
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    placeholder: t('landing.paqueteria.contratacion_paqueteria.numero_de_guias_separados_por_comas_placeholder'),
    cols: 12,
    customClass: 'inputLanding',
  },
  { slot: true, key: 'endForm' },
  {
    key: 'auth-check',
    type: 'checkbox',
    value: null,
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    slotContent: `<p class="text-white">${t('landing.paqueteria.contratacion_paqueteria.termino1')}</p>`,
  },
  {
    key: 'faq-check',
    type: 'checkbox',
    value: null,
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    slotContent: `<p class="text-white">${t('landing.paqueteria.contratacion_paqueteria.acepto_los')}
                    <a
                      target="_blank"
                      href="https://www.chubb.com/content/dam/chubb-sites/chubb-com/mx-es/condiciones-generales/global/documents/pdf/ad33-aba-d-transporte-de-carga-especifica.pdf"
                      class="text-black"
                    >
                      ${t('landing.paqueteria.contratacion_paqueteria.terminos_y_condiciones')}
                    </a>
                  </p>`,
  },
  {
    key: 'docs-check',
    type: 'checkbox',
    value: null,
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    slotContent: `<p class="text-white">${t('landing.paqueteria.contratacion_paqueteria.termino2')}</p>`,
  },
  {
    key: 'private-check',
    type: 'checkbox',
    value: null,
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    slotContent: `<p class="text-white">${t('landing.paqueteria.contratacion_paqueteria.termino3')}
                  <a href="https://www.chubb.com/content/dam/chubb-sites/chubb-com/mx-es/footer/privacy-notices/documents/pdf/aviso-de-privacidad-asegurados-chubb-seguros-mexico.pdf" target="_blank" class="text-black">${t('landing.paqueteria.contratacion_paqueteria.aqui')}</a></p>`,
  },
]);

onMounted(() => {
  const windowWidth = window.innerWidth;
  if (windowWidth <= 480) {
    fieldsForm3.value.forEach((field: any) => {
      field.cols = 12;
    });
  }
});

const handleOnSubmitRFC = async (data: any) => {
  if (data.updatesClientInputs) {
    formClientMXref.value!.updateFieldsInArray(
      formClientMXref.value!.fieldsForm2,
      data.updatesClientInputs,
      null,
    );
  }

  inputsHidden.value = false;

  if (!data.client) {
    store.setProp({ key: 'client', value: { rfc: data.rfc } });
  }
};

const handleOnSubmitRUT = async (data: any) => {
  if (data.updatesClientInputs) {
    formClientCLref.value!.updateFieldsInArray(
      formClientCLref.value!.fieldsForm2,
      data.updatesClientInputs,
      null,
    );
  }

  inputsHidden.value = false;

  if (!data.client) {
    store.setProp({ key: 'client', value: { rut: data.rut } });
  }
};

const handleOnSubmitCUIT = async (data: any) => {
  const checkCuil = validationCUIT(data.cuit);
  if (data.updatesClientInputs) {
    formClientARref.value!.updateFieldsInArray(
		formClientARref.value!.fieldsForm2,
      data.updatesClientInputs,
      null,
    );
  }
  inputsHidden.value = false;
  const tradeNameField = await formClientARref.value!.fieldsForm2
  tradeNameField.find((field: any) => field.key === 'tradeName').validations.rules.max = checkCuil.type === 'Persona Jurídica' ? 40 : 20;
  if (!data.client) {
    store.setProp({ key: 'client', value: { cuit: data.cuit } });
  }
};

const handleOnSubmitPolicies = ({ items, isFormValid }: any) => {
  if (currentDomainCountry.value === 'MEXICO') {
    handleOnSubmitPoliciesMX({ items, isFormValid });
  } else if (currentDomainCountry.value === 'CHILE') {
    handleOnSubmitPoliciesCL({ items, isFormValid });
  } else {
	handleOnSubmitPoliciesAR({ items, isFormValid });
  }
};

const handleOnSubmitPoliciesMX = async ({ items, isFormValid }: any) => {
  const { items: itemsForm1, isFormValid: isFormValidForm1 } =
    await formClientMXref.value!.getValues();

  if (!tokenCaptcha.value) {
    return (error.value = t('landing.paqueteria.contratacion_paqueteria.por_favor_verifica_el_recaptcha'));
  }

  if (!isFormValidForm1) {
    return (error.value = t('landing.paqueteria.contratacion_paqueteria.el_formulario_debe_estar_completo'));
  }
  if (!isFormValid) {
    return (error.value = t('landing.paqueteria.contratacion_paqueteria.el_formulario_debe_estar_completo'));
  }

  const { address, companyGPSId, email, phone1, tradeName } = itemsForm1;

  const { indemnificationLimit, packageNumbers } = items;

  const validationRfcAndName = evaluateNames(store.client.rfc, tradeName);

  const {
    clientMotherSurname,
    clientFathersSurname,
    clientNames,
    clientBirthOrConstDate,
    taxIdVerificationCode,
    namesError,
    isClientAPhysicalPerson,
  } = validationRfcAndName;

  if (namesError) {
	return (error.value = t('landing.paqueteria.contratacion_paqueteria.ingrese_su_nombre_completo_seguido_de'));
  }

  try {
    loading.value = true;

    const createOrUpdateClientData = {
      isClientAPhysicalPerson,
      clientMotherSurname,
      clientFathersSurname,
      clientNames,
      clientBirthOrConstDate,
      taxIdVerificationCode,
      namesError,
      address,
      email,
      tradeName,
      phone1,
      countryId: store.client.country.id,
      cityId: store.client.city.id,
      neighborhoodId: store.client.neighborhood.id,
      postCode: store.client.postCode,
      rfc: store.client.rfc,
      stateId: store.client.state.id,
      companyGPSId: store.client?.companyGPS?.id ?? companyGPSId.id,
      urlDomain: window.location.origin,
    };

    const client = await createOrUpdateClient(
      store.client.id,
      createOrUpdateClientData,
      'MX',
    );
    store.setProp({
      key: 'client',
      value: { ...store.client, id: client.data.client.id },
    });

    const packageNumbersClened = packageNumbers
      .split(',')
      .map((e: string) => e.trim())
      .filter((e: string) => e !== '');

    const createPolicyData = {
      tradeName,
      rfc: store.client.rfc,
      indemnificationLimit,
      companyGPSId: companyGPSId.id,
      packageNumbers: JSON.stringify(packageNumbersClened),
      token: tokenCaptcha.value,
    };

    const newPolicyId = await createPolicyPackage(
      client.data.client.id,
      client.data.manager.id,
      createPolicyData,
    );

    loading.value = false;

    if (newPolicyId) {
      policyId.value = newPolicyId;
      isOpenModal.value = true;
    }
  } catch (e) {
    error.value = t('global.ocurrio_un_error');
    loading.value = false;
  } finally {
    captchaRef.value?.reset();
  }
};

const handleOnSubmitPoliciesCL = async ({ items, isFormValid }: any) => {
  const { items: itemsForm2, isFormValid: isFormValidForm2 } =
    await formClientCLref.value!.getValues();

  if (!tokenCaptcha.value) {
    return (error.value = t('landing.paqueteria.contratacion_paqueteria.por_favor_verifica_el_recaptcha'));
  }

  if (!isFormValidForm2) {
    return (error.value = t('landing.paqueteria.contratacion_paqueteria.el_formulario_debe_estar_completo'));
  }
  if (!isFormValid) {
    return (error.value = t('landing.paqueteria.contratacion_paqueteria.el_formulario_debe_estar_completo'));
  }

  const {
    address,
    companyGPSId,
    email,
    phone1,
    tradeName,
    comuna,
    region,
    postCode,
  } = itemsForm2;

  const { indemnificationLimit, packageNumbers } = items;

  if (tradeName.split(' ').length < 2) {
    return (error.value = t('landing.paqueteria.contratacion_paqueteria.debe_proporcionar_al_menos_un_nombre'));
  }

  try {
    loading.value = true;
    const rutWithoutSpecialChars = store.client.rut.replace(/[-.]/g, '');

    const createOrUpdateClientData = {
      tradeName,
      address,
      email,
      phone1,
      location: comuna.nombre,
      postCode,
      rut: rutWithoutSpecialChars,
      regionProvince: region.nombre,
      companyGPSId: store.client?.companyGPS?.id ?? companyGPSId.id,
      urlDomain: window.location.origin,
    };

    const client = await createOrUpdateClient(
      store.client.id,
      createOrUpdateClientData,
      'CL',
    );
    store.setProp({
      key: 'client',
      value: { ...store.client, id: client.data.client.id },
    });

    const packageNumbersClened = packageNumbers
      .split(',')
      .map((e: string) => e.trim())
      .filter((e: string) => e !== '');

    const createPolicyData = {
      tradeName,
      rut: rutWithoutSpecialChars,
      indemnificationLimit,
      companyGPSId: companyGPSId.id,
      packageNumbers: JSON.stringify(packageNumbersClened),
      token: tokenCaptcha.value,
    };

    const newPolicyId = await createPolicyPackage(
      client.data.client.id,
      client.data.manager.id,
      createPolicyData,
      'CL',
    );
    if (newPolicyId) {
      policyId.value = newPolicyId;
      isOpenModal.value = true;
    }

    // window.location.replace(urlForPayment);
    loading.value = false;
  } catch (e: any) {
    // await captchaRef.value?.forceExpire();
    tokenCaptcha.value = null;
    error.value = e.message || t('global.ocurrio_un_error');
    loading.value = false;
  } finally {
    captchaRef.value?.reset();
  }
};

const handleOnSubmitPoliciesAR = async ({ items, isFormValid }: any) => {
  const { items: itemsForm2, isFormValid: isFormValidForm2 } =
    await formClientARref.value!.getValues();
 
  if (!tokenCaptcha.value) {
    return (error.value = t('landing.paqueteria.contratacion_paqueteria.por_favor_verifica_el_recaptcha'));
  }

  if (!isFormValidForm2) {
    return (error.value = t('landing.paqueteria.contratacion_paqueteria.el_formulario_debe_estar_completo'));
  }
  if (!isFormValid) {
    return (error.value = t('landing.paqueteria.contratacion_paqueteria.el_formulario_debe_estar_completo'));
  }

  const {
    address,
    companyGPSId,
    email,
    phone1,
    tradeName,
    city,
    province,
    postCode,
  } = itemsForm2;

  const { indemnificationLimit, packageNumbers } = items;

  if (tradeName.split(' ').length < 2) {
    return (error.value = t('landing.paqueteria.contratacion_paqueteria.debe_proporcionar_al_menos_un_nombre'));
  }

  try {
    loading.value = true;

    const createOrUpdateClientData = {
      tradeName,
      address,
      email,
      phone1,
      location: city.nombre ?? city,
      postCode,
      cuit: store.client.cuit,
	  regionProvince: province.nombre,
      companyGPSId: store.client?.companyGPS?.id ?? companyGPSId.id,
      urlDomain: window.location.origin,
    };

    const client = await createOrUpdateClient(
      store.client.id,
      createOrUpdateClientData,
      'AR',
    );
    store.setProp({
      key: 'client',
      value: { ...store.client, id: client.data.client.id },
    });

    const packageNumbersClened = packageNumbers
      .split(',')
      .map((e: string) => e.trim())
      .filter((e: string) => e !== '');

    const createPolicyData = {
      tradeName,
	  cuit: store.client.cuit,
      indemnificationLimit,
      companyGPSId: companyGPSId.id,
      packageNumbers: JSON.stringify(packageNumbersClened),
      token: tokenCaptcha.value,
    };

    const newPolicyId = await createPolicyPackage(
      client.data.client.id,
      client.data.manager.id,
      createPolicyData,
      'AR',
    );
    if (newPolicyId) {
      policyId.value = newPolicyId;
      isOpenModal.value = true;
    }

    // window.location.replace(urlForPayment);
    loading.value = false;
  } catch (e: any) {
    // await captchaRef.value?.forceExpire();
    tokenCaptcha.value = null;
    error.value = e.message || t('global.ocurrio_un_error');
    loading.value = false;
  }finally{
	 captchaRef.value?.reset()
  }
};

const hideModal = () => {
  isOpenModal.value = false;
  return router.push('/');
};

const handleGetPDFCoverage = () => {
  getPDFCoverage(policyId.value, 'Paqueteria');
};

const handleTokenCaptcha = (token: string) => {
  tokenCaptcha.value = token;
};

const handleOnExpired = () => {
  tokenCaptcha.value = null;
};

const handleAllowEndDate = async (e: any) => {
  fieldsForm3.value.find(
    (field: any) => field.key === 'travelEndDate',
  ).disabled = e.value ? false : true;
  fieldsForm3.value.find((field: any) => field.key === 'travelEndDate').min =
    new Date(`${e.value}T00:00:00`);
  await refForm3.value.setValue({ key: 'travelEndDate', value: null });
};
</script>

<template>
  <h1 class="landing__title">{{ t('landing.paqueteria.contratacion_paqueteria.seguro_de_mercancias_por_trayecto') }}</h1>
  <h2 class="landing__subtitle">{{ t('landing.paqueteria.contratacion_paqueteria.ya_falta_menos') }}</h2>
  <h2 class="landing__subtitle">{{ t('landing.paqueteria.contratacion_paqueteria.ayudanos_completando') }}</h2>
  <div class="contratacion">
    <div class="contratacion__container">
      <h2 class="text-center text-white">{{ t('landing.paqueteria.contratacion_paqueteria.datos_del_cliente') }}</h2>
      <FormRFCMX
        @submitRFC="handleOnSubmitRFC"
        :inputsHidden="inputsHidden"
        :companySelectedByParams="companySelectedByParams"
        v-if="currentDomainCountry === 'MEXICO'"
      />
      <FormRUTCL
        @submitRUT="handleOnSubmitRUT"
        :inputsHidden="inputsHidden"
        :companySelectedByParams="companySelectedByParams"
        v-if="currentDomainCountry === 'CHILE'"
      />
      <FormCUITAR
        @submitCUIT="handleOnSubmitCUIT"
        :inputsHidden="inputsHidden"
        :companySelectedByParams="companySelectedByParams"
        v-if="currentDomainCountry === 'ARGENTINA'"
      />
      <FormClientMX
        ref="formClientMXref"
        v-show="!inputsHidden"
        v-if="currentDomainCountry === 'MEXICO'"
      />
      <FormClientCL
        ref="formClientCLref"
        v-show="!inputsHidden"
        v-if="currentDomainCountry === 'CHILE'"
      />
      <FormClientAR
        ref="formClientARref"
        v-show="!inputsHidden"
        v-if="currentDomainCountry === 'ARGENTINA'"
      />

      <Form
        ref="refForm3"
        :inputs="fieldsForm3"
        @onSubmit="handleOnSubmitPolicies"
        @datePicker="handleAllowEndDate"
        v-show="!inputsHidden"
      >
        <template #title>
          <h2 class="text-center text-white mt-5">{{ t('landing.paqueteria.contratacion_paqueteria.datos_del_viaje') }}</h2>
        </template>
        <template #endForm>
          <h2 class="text-center text-white mt-2 fs-5">
            {{ t('landing.paqueteria.contratacion_paqueteria.seguro_propiedad') }}
          </h2>
        </template>

        <template #buttons>
          <p v-if="error" class="text-danger-emphasis mt-3">{{ error }}</p>
		  <CaptchaCloudflare
		     ref="captchaRef"
			 @verified="handleTokenCaptcha"
			 @expired="handleOnExpired"
		  />
          <div class="d-flex">
            <CButton
              class="p-4 w-100 my-2 px-2"
              background="background-green"
              style="font-size: 1.5rem"
            >
              <template #content>
                <div>
                  <BSpinner v-if="loading" />
                  <span v-else>{{ t('landing.paqueteria.contratacion_paqueteria.contratar_el_seguro') }}</span>
                </div>
              </template>
            </CButton>
          </div>
        </template>
      </Form>
    </div>
  </div>
  <Footer />
  <WhatsappButton />
  <BModal
    :modelValue="isOpenModal"
    hide-footer
    static
    @hide="hideModal"
    size="lg"
    v-if="policyId"
  >
    <h3>{{ t('landing.paqueteria.contratacion_paqueteria.felicitaciones_poliza_generada') }}</h3>
    <p>{{ t('landing.paqueteria.contratacion_paqueteria.numero_transaccion') }} {{ policyId }}/{{ new Date().getFullYear() }}</p>
    <p>
		{{ t('landing.paqueteria.contratacion_paqueteria.carta_cobertura_enviada') }}
      <a class="underline-link cursor-pointer" @click="handleGetPDFCoverage"
        >{{ t('landing.paqueteria.contratacion_paqueteria.aqui') }}</a
      >
    </p>
    <p>
		{{ t('landing.paqueteria.contratacion_paqueteria.realiza_pago_para_activar') }}
    </p>
    <h3>{{ t('landing.paqueteria.contratacion_paqueteria.datos_bancarios_titulo') }}</h3>
    <ul>
      <li>{{ t('landing.paqueteria.contratacion_paqueteria.banco_nombre') }}</li>
      <li>{{ t('landing.paqueteria.contratacion_paqueteria.banco_entidad') }}</li>
      <li>{{ t('landing.paqueteria.contratacion_paqueteria.banco_cuenta') }}</li>
      <li>{{ t('landing.paqueteria.contratacion_paqueteria.banco_clave') }}</li>
      <li>{{ t('landing.paqueteria.contratacion_paqueteria.banco_referencia') }} {{ policyId }}/{{ new Date().getFullYear() }}</li>
    </ul>
    <p style="color: red">
		{{ t('landing.paqueteria.contratacion_paqueteria.atencion_seguro_validez') }}
    </p>
    <p>
		{{ t('landing.paqueteria.contratacion_paqueteria.ayuda_pago_contacto') }}
      <a class="underline-link">+52 55 4619 4648</a>
    </p>
  </BModal>
</template>
